<template>
  <div class="submissions pane">
    <h3 v-text="queueMode ? 'Queue' : 'Submissions'"></h3>

    <div class="filters" v-if="!queueMode">
      <label>
        <span>Show</span>
        <select v-model="filter">
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="archived">Archived</option>
        </select>
      </label>
      <label>
        <span>Sort by</span>
        <select v-model="sortBy">
          <option value="submitDate">Date</option>
          <option value="score">Score</option>
        </select>
      </label>
      <label>
        <select v-model.number="sortDir">
          <option value="-1">Descending</option>
          <option value="1">Ascending</option>
        </select>
      </label>
      <label>
        <span>In</span>
        <select v-model="selectedLane">
          <option :value="null" v-text="'All lanes'" />
          <option
            v-for="lane of lanes"
            :key="lane.id"
            :value="lane"
            v-text="lane.title" />
        </select>
      </label>
    </div>

    <div v-if="queueMode" class="queue">
      <div class="filters">
        <label>
          <span>Lane:</span>
          <select v-model="selectedLane">
            <option :value="null" v-text="'All lanes'" />
            <option
              v-for="lane of lanes"
              :key="lane.id"
              :value="lane"
              v-text="lane.title" />
          </select>
        </label>
      </div>
      <div
        v-for="(submission) of filteredSubmissions"
        :key="submission.id"
        :class="['row', { active: submission.active }]"
      >
        <span class="score">{{ submission.score }}</span>
        <span class="heading">
          <div class="author">{{ submission.authorName }}</div>
          <div class="title">
            {{ submission.title }}
          </div>
          <div class="other-props">
            <span
              v-if="submission.product"
              class="product"
              v-text="submission.product.label" />
            <span
              v-for="upsell of submission.upsells"
              :key="upsell.key"
              :class="['upsell', upsell.key]"
              v-text="upsell.label" />
            <template v-if="submission.template === 'mtga'">
              <span
                v-for="color of submission.details.colors"
                :key="color"
                :class="['color', color]">
                <i :class="['icon', getColorIcon(color)]"></i>
              </span>
            </template>
          </div>
        </span>
        <div class="description">
          <template v-if="submission.details.description">
            {{ submission.details.description.substr(0, 500) }}
            <template v-if="submission.details.description.length > 500">
              ...
              <a
                href="#"
                @click.prevent="toggleModal(submission.details.description)">
                more
              </a>
            </template>
          </template>
        </div>
        <span class="actions">
          <button
            v-if="submission.details.list"
            @click="toggleModal(submission.details.list)"
            class="action view">
            <i class="icon fas fa-eye"></i> View
          </button>
          <button
            v-if="submission.details.list"
            @click="copyText(submission.details.list)"
            class="action copy">
            <i class="icon fas fa-clipboard"></i> Copy
          </button>
          <button
            v-if="!submission.active && isAdmin"
            class="action activate"
            @click="activate(submission)">
            <i class="icon fas fa-star"></i> Activate
          </button>
          <button
            v-if="submission.active && isAdmin"
            class="action archive"
            @click="archive(submission)">
            <i class="icon fas fa-lock"></i> Archive
          </button>
          <button
            v-if="isAdmin"
            class="action edit"
            @click="editSubmission(submission)">
            <i class="icon fas fa-pencil"></i> Edit
          </button>
        </span>
      </div>
      <div
        class="modal"
        :class="{ active: modalActive }"
        @click.self="toggleModal()">
        <div class="pane">
          <textarea
            v-model.lazy="modalContent"
            rows="25"
            cols="60"></textarea>
          <br />
          <button @click="copyText(modalContent)">Copy to clipboard</button>
          <button @click="toggleModal()">Go away</button>
        </div>
      </div>
    </div>
    <div v-else class="list">
      <table>
        <thead>
          <tr>
            <th v-if="!selectedLane">Lane</th>
            <th>Title</th>
            <th>Author</th>
            <th>Status</th>
            <th>Base Cost</th>
            <th>Date</th>
            <th>Score</th>
            <th>Details</th>
            <th>Type</th>
            <th>Upsells</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="submission of filteredSubmissions" :key="submission.id">
            <td v-if="!selectedLane" class="lane">
              {{ getLaneName(submission.lane) }}
            </td>
            <td class="title">
              <router-link :to="`/submissions/${submission.id}`">{{ submission.title }}</router-link>
            </td>
            <td class="author">
              <router-link :to="`/users/${submission.authorId}`">
                {{ submission.authorName }}
              </router-link>
            </td>
            <td class="status" :class="submission.status">
              <span :class="['icon emoji', getStatus(submission)]"></span>
              {{ getStatus(submission) }}
            </td>
            <td class="cost">{{ submission.cost }}</td>
            <td
              class="date"
              :title="submission.created.toDate() | moment('MM/DD/YY H:mma [(UTC ]Z)')">
              {{ submission.created.toDate() | moment('from') }}
            </td>
            <td class="score" :title="`${submission.cost} base + ${getScoreBumps(submission)} bumps + ${getScoreAge(submission)} days`">
              {{ submission.score }}
            </td>
            <td class="details">
              <div class="colors" v-if="submission.template === 'mtga'">
                <span
                  v-for="color of submission.details.colors"
                  :key="color"
                  :class="['color', color]">
                  <i :class="['icon', getColorIcon(color)]"></i>
                </span>
              </div>
              <div v-if="submission.details.description">
                {{ submission.details.description.substr(0, 200) }}
                <template v-if="submission.details.description.length > 200">
                  ...
                  <a
                    href="#"
                    @click.prevent="toggleModal(submission.details.description)">
                    more
                  </a>
                </template>
              </div>
            </td>
            <td class="products">
              <span
                v-if="submission.product"
                v-text="submission.product.label"
                class="product" />
            </td>
            <td class="upsells">
              <template v-if="submission.upsells">
                <span
                  v-for="upsell of submission.upsells"
                  :key="upsell.key"
                  class="upsell"
                  v-text="upsell.label" />
              </template>
            </td>
            <td>
              <div class="actions">
                <button
                  v-if="submission.status === 'pending'"
                  class="action approve"
                  @click="approve(submission)">
                  <i class="icon fas fa-check"></i> Approve
                </button>
                <button
                  v-if="isAdmin && submission.active"
                  class="action archive"
                  @click="archive(submission)">
                  <i class="icon fas fa-lock"></i> Archive
                </button>
                <button
                  v-if="isAdmin || submission.status === 'pending'"
                  class="action refund"
                  @click="deleteSubmission(submission)">
                  <i class="icon fas fa-trash"></i> Refund
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import DeckHelper from '../mixins/DeckHelper'
import ApiHelper from '../mixins/ApiHelper'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export default {
  name: 'Submissions',
  props: ['submissions', 'queueMode', 'defaultFilter'],
  mixins: [DeckHelper, ApiHelper],

  data () {
    if (this.queueMode) {
      return {
        selectedLane: null,
        modalActive: false,
        modalContent: '',
        filter: 'approved',
        sortBy: 'score',
        sortDir: -1
      }
    }
    return {
      selectedLane: null,
      modalActive: false,
      modalContent: '',
      filter: this.defaultFilter || 'all',
      sortBy: 'submitDate',
      sortDir: -1
    }
  },

  watch: {
    $route () {
      if (!this.queueMode) {
        this.filter = this.defaultFilter || 'all'
      }
      this.selectedLane = this.queueMode ? this.defaultLane : null
    }
  },

  computed: {
    ...mapGetters(['isAdmin', 'defaultLane', 'getLaneById']),
    ...mapState(['lanes']),

    filteredSubmissions () {
      let { filter, sortBy, sortDir } = this
      if (this.queueMode) {
        filter = 'approved'
        sortBy = 'score'
        sortDir = -1
      }

      const submissions = (this.submissions || this.$store.state.submissions)
        .slice(0)
        .filter((submission) => {
          if (!this.selectedLane) {
            return submission
          }
          return submission.lane === this.selectedLane.id
        })
        .map((submission) => {
          return {
            ...submission,
            id: submission.id,
            score: this.getScore(submission),
            submitDate: submission.created.toDate()
          }
        })

      if (filter === 'all') {
        return this.sort(submissions, sortBy, sortDir)
      }

      const filtered = submissions
        .filter((submission) => {
          if (filter === 'archived') {
            return submission.archived === true
          }

          return submission.archived === false && submission.status === filter
        })

      return this.sort(filtered, sortBy, sortDir)
    }
  },

  methods: {
    sort (items, field, direction = -1) {
      const copy = items.slice(0)
      copy.sort((a, b) => Math.sign(a[field] - b[field]) * direction)
      return copy
    },

    activate (submission) {
      this.apiRequest(`submission/${submission.id}/activate`, {
        method: 'post'
      }).catch((err) => console.error(err))
    },

    approve (submission) {
      this.apiRequest(`submission/${submission.id}/approve`, {
        method: 'post'
      }).catch((err) => console.error(err))
    },

    archive (submission) {
      this.apiRequest(`submission/${submission.id}/archive`, {
        method: 'post'
      }).catch((err) => console.error(err))
    },

    deleteSubmission(submission) {
      if (
        !confirm('Are you sure you want to permanently delete this submission?')
      ) {
        return
      }
      const refund = confirm('Refund all points spent on this submission? This includes the initial cost, submission fee, and any points spent on bumping it.')
      this.apiRequest(`submission/${submission.id}`, {
        method: 'delete',
        data: {
          refund
        }
      })
    },

    editSubmission (submission) {
      this.$router.push(`/submissions/${submission.id}`)
    },

    copyText (str) {
      const $el = document.createElement('textarea')
      $el.value = str
      document.body.appendChild($el)
      $el.select()
      document.execCommand('copy')
      document.body.removeChild($el)
    },

    toggleModal (content) {
      if (content) {
        this.modalContent = content
        return this.modalActive = true
      }
      this.modalActive = !this.modalActive
    },

    getLaneName (laneId) {
      const lane = this.getLaneById(laneId)
      return lane ? lane.title : laneId
    }
  },

  created () {
    this.selectedLane = this.queueMode ? this.defaultLane : null
  }
}
</script>

<style scoped>
.filters label {
  padding: 0 0.25em;
}
.filters select {
  border: 0;
  background: transparent;
  font: inherit;
  text-align: right;
  color: green;
}
th {
  text-align: left;
}
th,
td {
  padding: 0 0.25em;
}
td.id {
  font-size: smaller;
  font-family: monospace;
}
td.description {
  max-width: 400px;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  display: -webkit-box;
}

.description {
  font-size: smaller;
}

.queue .row {
  display: grid;
  grid-template-columns: 50px 300px auto 200px;
  margin: 0 1em;
  padding: 1em 0;
  border-top: 1px solid #ccc;
}
.queue .score {
  display: flex;
  align-items: center;
  /* justify-content: center; */
  font-size: larger;
}
.queue .title {
  font-size: larger;
  font-weight: 600;
}
.queue .row.active {
  background-color: #eeffee;
}

.list .status {
  white-space: nowrap;
}
.list .status .icon.emoji {
  width: 2em;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.color {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 1em;
  height: 1em;
  background-color: gray;
}
.color .icon { font-size: 0.7em; }
.color.W { background-color: lightgoldenrodyellow; }
.color.B { background-color: lightgray; }
.color.U { background-color: lightskyblue; }
.color.R { background-color: lightcoral; }
.color.G { background-color: lightgreen; }

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #0006;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 100ms ease-out;
  opacity: 0;
}
.modal:not(.active) {
  pointer-events: none;
}
.modal.active {
  opacity: 1;
}
.modal .pane {
  background: white;
  padding: 1em;
  border-radius: 4px;
  box-shadow: 0 2px 20px #0006;
}

.product,
.upsell {
  margin: 1px;
  padding: 2px 4px;
  background-color: #fee;
  color: deeppink;
  text-transform: uppercase;
  display: inline-block;
  font-size: 10px;
}

.actions {
  display: flex;
}
.action.edit .icon { color: orange; }
.action.activate .icon { color: gold; }
.action.archive .icon { color: cornflowerblue; }
.action.approve .icon { color: green; }
.action.view .icon { color: darkslateblue; }
.action.copy .icon { color: darkslategray; }
.action.refund .icon { color: crimson; }

tr:hover {
  background-color: #eee;
}
</style>
